import React, {useEffect, useState} from 'react';
import {Button} from '@material-ui/core';
import CheckIcon from 'components/CheckIcon/CheckIcon';
import {Route} from 'react-router-dom';
import {IFdApplicationType, IFdFundingRequestCost} from 'constants/interfaces';
import {
  apiCreateFdFundingAsk,
  apiGetFdFundingAsksByApplicationId,
} from 'constants/Api';
import {FundingAskForm} from 'components/applications/FundingAskForm';
import {
  reduxToggleSidePanel,
  reduxSetSidePanelEditState,
} from 'appredux/actions';

function buildFundingOptions(
  app?: IFdApplicationType,
): IFdFundingRequestCost[] {
  if (!app) return [];

  let seed = 0;
  let fert = 0;
  let chem = 0;
  (app.productionCosts || []).forEach((c) => {
    seed += c.seed;
    fert += c.fertiliser;
    chem += c.chemical;
  });

  const prod: IFdFundingRequestCost[] = [
    {id: 'seed', label: 'Seed', cost: seed},
    {id: 'fertiliser', label: 'Fertiliser', cost: fert},
    {id: 'chemical', label: 'Chemicals', cost: chem},
  ];

  const op: IFdFundingRequestCost[] = (app.operatingCosts || [])
    .filter((o) => o.year === 2025)
    .map((o) => {
      const parsed = JSON.parse(o.monthlyCosts);
      return {
        id: String(o.id),
        label: o.expense?.name || '',
        cost: Number(parsed.total) || 0,
      };
    });

  return [...prod, ...op];
}

const AddOrUpdateFdFundingAsk: React.FC<{
  application: IFdApplicationType;
  applicationId: number;
}> = ({application, applicationId}) => {
  const [options, setOptions] = useState<IFdFundingRequestCost[]>([]);
  const [initialSelected, setInitialSelected] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setOptions(buildFundingOptions(application));

    apiGetFdFundingAsksByApplicationId(applicationId).then((ask) => {
      if (ask?.fundingRequestCosts?.length) {
        setInitialSelected(ask.fundingRequestCosts.map((c) => c.id));
      }
    });
  }, [application, applicationId]);

  const isAdding = initialSelected.length === 0;

  const handleSubmit = async (selectedIds: string[], notes: string) => {
    setIsLoading(true);
    await apiCreateFdFundingAsk({
      applicationId,
      notes,
      fundingRequestCosts: options.filter((o) => selectedIds.includes(o.id)),
    }).catch(console.error);
    setIsLoading(false);
    setSaved(true);
    reduxSetSidePanelEditState('saved');
  };

  if (saved) {
    return (
      <Route
        render={() => (
          <div className="side-panel-success-container">
            <div className="mid-section">
              <div className="success-gif">
                <CheckIcon />
              </div>
              <div className="success-msg">
                {isAdding
                  ? 'Funding ask added successfully'
                  : 'Funding ask updated successfully'}
              </div>
            </div>
            <div className="btn-container">
              <Button
                className="primary-btn"
                variant="outlined"
                onClick={() => reduxToggleSidePanel(false, null)}
              >
                Done
              </Button>
            </div>
          </div>
        )}
      />
    );
  }

  return (
    <FundingAskForm
      options={options}
      initialSelected={initialSelected}
      isLoading={isLoading}
      submitLabel={isAdding ? 'Add' : 'Update'}
      cancelLabel="Cancel"
      onCancel={() => reduxToggleSidePanel(false, null)}
      onSubmit={handleSubmit}
    />
  );
};

export default AddOrUpdateFdFundingAsk;
